import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiHome, FiGrid, FiPhone, FiX, FiMenu, FiMonitor, FiBox, FiGift, FiUsers, FiChevronDown, FiMail, FiTruck, FiRotateCcw, FiPackage, FiBriefcase } from 'react-icons/fi';

const Header = () => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isWhoWeServeOpen, setIsWhoWeServeOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isMobileWhoWeServeOpen, setIsMobileWhoWeServeOpen] = useState(false);
  const headerRef = useRef(null);

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
    setIsWhoWeServeOpen(false);
  };

  const closeServices = () => {
    setIsServicesOpen(false);
  };

  const toggleWhoWeServe = () => {
    setIsWhoWeServeOpen(!isWhoWeServeOpen);
    setIsServicesOpen(false);
  };

  const closeWhoWeServe = () => {
    setIsWhoWeServeOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isServicesOpen) {
      setIsServicesOpen(false);
    }
    if (isMobileMenuOpen) {
      setIsMobileServicesOpen(false);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsWhoWeServeOpen(false);
    setIsMobileServicesOpen(false);
    setIsMobileWhoWeServeOpen(false);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="header" ref={headerRef}>
      <div className="header-content">
        <Link to="/" className="logo" aria-label="Globoflo">
          <img src="/globoflo.png" alt="Globoflo" className="logo-image" />
        </Link>
        
        {/* Mobile Menu Toggle */}
        <button 
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>

        {/* Desktop Navigation */}
        <nav className="desktop-nav" aria-label="Primary">
          <ul className="nav">
            <li>
              <Link to="/">
                <span className="nav-icon" aria-hidden><FiHome size={16} /></span>
                <span>Home</span>
              </Link>
            </li>
            <li className="dropdown-container">
              <button 
                className="dropdown-trigger"
                onClick={toggleServices}
                onMouseEnter={() => {
                  setIsServicesOpen(true);
                  setIsWhoWeServeOpen(false);
                }}
                aria-expanded={isServicesOpen}
                aria-haspopup="true"
              >
                <span className="nav-icon" aria-hidden><FiGrid size={16} /></span>
                <span style={{position:'relative', top:'1px'}}>Solutions</span>
                <span className="caret" aria-hidden><FiChevronDown size={14} /></span>
              </button>
              <div 
                className={`dropdown-menu ${isServicesOpen ? 'open' : ''}`}
                onMouseLeave={closeServices}
                onMouseEnter={() => {
                  setIsServicesOpen(true);
                  setIsWhoWeServeOpen(false);
                }}
              >
                <div className="dropdown-content">
                  <div className="dropdown-column">
                    <Link to="/services/abandoned-freight" className="dropdown-item" onClick={closeServices}>
                      <div className="dropdown-icon"><FiTruck size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Abandoned Freight</h4>
                        <p>Resolve dock congestion and recover value</p>
                      </div>
                    </Link>
                    
                    <Link to="/services/store-closing" className="dropdown-item" onClick={closeServices}>
                      <div className="dropdown-icon"><FiBox size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Store Closing</h4>
                        <p>Planned closures, rapid value recovery</p>
                      </div>
                    </Link>

                    <Link to="/services/customer-returns" className="dropdown-item" onClick={closeServices}>
                      <div className="dropdown-icon"><FiRotateCcw size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Customer Returns</h4>
                        <p>Convert returns into recoverable assets</p>
                      </div>
                    </Link>
                  </div>
                  
                  <div className="dropdown-column">
                    <Link to="/services/itad" className="dropdown-item" onClick={closeServices}>
                      <div className="dropdown-icon"><FiMonitor size={18} /></div>
                      <div className="dropdown-text">
                        <h4>IT Disposition</h4>
                        <p>Secure data destruction & asset recovery</p>
                      </div>
                    </Link>

                    <Link to="/services/donations" className="dropdown-item" onClick={closeServices}>
                      <div className="dropdown-icon"><FiGift size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Charitable Donations</h4>
                        <p>Gain tax benefits while helping communities</p>
                      </div>
                    </Link>

                    <Link to="/services/excessive-inventory" className="dropdown-item" onClick={closeServices}>
                      <div className="dropdown-icon"><FiPackage size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Excessive Inventory</h4>
                        <p>Turn overstock into recovered value</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </li>
            
            <li className="dropdown-container">
              <button 
                className="dropdown-trigger"
                onClick={toggleWhoWeServe}
                onMouseEnter={() => {
                  setIsWhoWeServeOpen(true);
                  setIsServicesOpen(false);
                }}
                aria-expanded={isWhoWeServeOpen}
                aria-haspopup="true"
              >
                <span className="nav-icon" aria-hidden><FiUsers size={16} /></span>
                <span style={{position:'relative', top:'1px'}}>Who We Serve</span>
                <span className="caret" aria-hidden><FiChevronDown size={14} /></span>
              </button>
              <div 
                className={`dropdown-menu ${isWhoWeServeOpen ? 'open' : ''}`}
                onMouseLeave={closeWhoWeServe}
                onMouseEnter={() => {
                  setIsWhoWeServeOpen(true);
                  setIsServicesOpen(false);
                }}
              >
                <div className="dropdown-content">
                  <div className="dropdown-column">
                    <Link to="/who-we-serve/3pl-distributors" className="dropdown-item" onClick={closeWhoWeServe}>
                      <div className="dropdown-icon"><FiTruck size={18} /></div>
                      <div className="dropdown-text">
                        <h4>3PL & Distributors</h4>
                        <p>Logistics and distribution solutions</p>
                      </div>
                    </Link>
                    
                    <Link to="/who-we-serve/enterprise" className="dropdown-item" onClick={closeWhoWeServe}>
                      <div className="dropdown-icon"><FiUsers size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Enterprise</h4>
                        <p>Large-scale asset management</p>
                      </div>
                    </Link>

                    <Link to="/who-we-serve/nonprofit" className="dropdown-item" onClick={closeWhoWeServe}>
                      <div className="dropdown-icon"><FiGift size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Nonprofit & Charity</h4>
                        <p>Supporting mission-driven organizations</p>
                      </div>
                    </Link>
                  </div>
                  
                  <div className="dropdown-column">
                    <Link to="/who-we-serve/retail-ecommerce" className="dropdown-item" onClick={closeWhoWeServe}>
                      <div className="dropdown-icon"><FiBox size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Retail & Ecommerce</h4>
                        <p>Returns and inventory solutions</p>
                      </div>
                    </Link>

                    <Link to="/who-we-serve/manufacturing" className="dropdown-item" onClick={closeWhoWeServe}>
                      <div className="dropdown-icon"><FiPackage size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Manufacturing</h4>
                        <p>Industrial asset recovery</p>
                      </div>
                    </Link>

                    <Link to="/who-we-serve/small-business" className="dropdown-item" onClick={closeWhoWeServe}>
                      <div className="dropdown-icon"><FiBriefcase size={18} /></div>
                      <div className="dropdown-text">
                        <h4>Small Business</h4>
                        <p>Enterprise-level solutions for small businesses</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </li>
            <li>
              <Link to="/contact">
                <span className="nav-icon" aria-hidden><FiMail size={16} /></span>
                <span>Contact</span>
              </Link>
            </li>
          </ul>
        </nav>
        <div className="header-actions">
          <a href="tel:+18778739336" className="phone">
            <span className="nav-icon" aria-hidden><FiPhone size={16} /></span>
            <span>(877) 873-9336</span>
          </a>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
            <div className="mobile-menu-header">
              <div className="mobile-logo">
                <img src="/globoflo.png" alt="Globoflo" className="mobile-logo-image" />
              </div>
              <button 
                className="mobile-menu-close"
                onClick={closeMobileMenu}
                aria-label="Close mobile menu"
              >
                <FiX size={24} />
              </button>
            </div>
            
            <div className="mobile-menu-divider"></div>
            
            <nav className="mobile-nav">
              <ul className="mobile-nav-list">
                <li>
                  <Link to="/" onClick={closeMobileMenu}>
                    <span className="mobile-nav-icon" aria-hidden><FiHome size={20} /></span>
                    <span>Home</span>
                  </Link>
                </li>
                
                {/* Mobile Solutions dropdown */}
                <li>
                  <button
                    className="mobile-nav-dropdown"
                    onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                    aria-expanded={isMobileServicesOpen}
                    aria-controls="mobile-solutions-submenu"
                  >
                    <span className="mobile-nav-icon" aria-hidden><FiGrid size={20} /></span>
                    <span>Solutions</span>
                    <span className="mobile-caret" aria-hidden><FiChevronDown /></span>
                  </button>
                  <ul
                    id="mobile-solutions-submenu"
                    className={`mobile-sublist ${isMobileServicesOpen ? 'open' : ''}`}
                    aria-hidden={!isMobileServicesOpen}
                  >
                    <li><Link to="/services/abandoned-freight" onClick={closeMobileMenu}>Abandoned Freight</Link></li>
                    <li><Link to="/services/store-closing" onClick={closeMobileMenu}>Store Closing</Link></li>
                    <li><Link to="/services/customer-returns" onClick={closeMobileMenu}>Customer Returns</Link></li>
                    <li><Link to="/services/itad" onClick={closeMobileMenu}>IT Disposition</Link></li>
                    <li><Link to="/services/donations" onClick={closeMobileMenu}>Charitable Donations</Link></li>
                    <li><Link to="/services/excessive-inventory" onClick={closeMobileMenu}>Excessive Inventory</Link></li>
                  </ul>
                </li>

                {/* Mobile Who We Serve dropdown */}
                <li>
                  <button
                    className="mobile-nav-dropdown"
                    onClick={() => setIsMobileWhoWeServeOpen(!isMobileWhoWeServeOpen)}
                    aria-expanded={isMobileWhoWeServeOpen}
                    aria-controls="mobile-who-we-serve-submenu"
                  >
                    <span className="mobile-nav-icon" aria-hidden><FiUsers size={20} /></span>
                    <span>Who We Serve</span>
                    <span className="mobile-caret" aria-hidden><FiChevronDown /></span>
                  </button>
                  <ul
                    id="mobile-who-we-serve-submenu"
                    className={`mobile-sublist ${isMobileWhoWeServeOpen ? 'open' : ''}`}
                    aria-hidden={!isMobileWhoWeServeOpen}
                  >
                    <li><Link to="/who-we-serve/3pl-distributors" onClick={closeMobileMenu}>3PL & Distributors</Link></li>
                    <li><Link to="/who-we-serve/enterprise" onClick={closeMobileMenu}>Enterprise</Link></li>
                    <li><Link to="/who-we-serve/nonprofit" onClick={closeMobileMenu}>Nonprofit & Charity</Link></li>
                    <li><Link to="/who-we-serve/retail-ecommerce" onClick={closeMobileMenu}>Retail & Ecommerce</Link></li>
                    <li><Link to="/who-we-serve/manufacturing" onClick={closeMobileMenu}>Manufacturing</Link></li>
                    <li><Link to="/who-we-serve/small-business" onClick={closeMobileMenu}>Small Business</Link></li>
                  </ul>
                </li>

                <li>
                  <Link to="/contact" onClick={closeMobileMenu}>
                    <span className="mobile-nav-icon" aria-hidden><FiMail size={20} /></span>
                    <span>Contact</span>
                  </Link>
                </li>
                
                <li>
                  <a href="tel:+18778739336" onClick={closeMobileMenu}>
                    <span className="mobile-nav-icon" aria-hidden>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </span>
                    <span>(877) 873-9336</span>
                  </a>
                </li>
              </ul>
            </nav>
            
            <div className="mobile-menu-divider"></div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
